import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

import { EFieldInputTypes, ExtraToChangeOnChangeForm, IField, OnChangeHandler } from "./types";
import { debounce, throttle } from "./validations/helper";

export function useForm<T, N = unknown>(initialForm: T, fields?: IField<N, unknown, T>[], extraToChangeOnChange?: ExtraToChangeOnChangeForm): [T, OnChangeHandler, Dispatch<SetStateAction<T>>] {
  const [form, setForm] = useState(() => initialForm || ({} as T));

  const onChange = useCallback(
    ({ target: { name, value, type, checked, files } }: { target: { name: string; value: any; type: string; checked?: boolean; files?: FileList | null } }, extraToChange = {}) => {
      setForm((prev) => {
        // Create a mutable copy that TypeScript will accept for property assignments
        const newForm = { ...prev } as unknown as Record<string, any>;
        
        // Set the form field value based on input type
        newForm[name] = type === EFieldInputTypes.Checkbox ? checked : type === "file" ? files : value;
        
        // Add extraToChange properties
        Object.assign(newForm, extraToChange);

        const field = fields?.find((f) => f.name === name);

        if (field?.dependencies)
          for (const dpd of field.dependencies) {
            // Handle dependency values
            newForm[dpd.name as any] = "initialValue" in dpd ? dpd.initialValue : "";
            if (dpd.viewName) newForm[dpd.viewName] = "";
          }

        if (extraToChangeOnChange) {
          // Apply extra changes based on current form state
          const extras = extraToChangeOnChange(newForm as unknown as any);
          for (const key in extras) {
            newForm[key] = extras[key];
          }
        }

        // Cast back to the expected type
        return newForm as unknown as T;
      });
    },
    [fields, extraToChangeOnChange]
  );

  return [form, onChange as any, setForm];
}

// eslint-disable-next-line no-empty-function
const defaultDependencies: any[] = [];

export function useDebounce<F>(func: F, dependencies = defaultDependencies, delay = 500) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const funcToRun = useCallback(func as unknown as (...args: any[]) => any, [...dependencies]);
  const debouncedFunction = useMemo<F>(() => debounce(funcToRun, delay) as unknown as F, [funcToRun, delay]);

  return debouncedFunction;
}
export function useThrottle<F>(func: F, dependencies = defaultDependencies, delay = 500) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const funcToRun = useCallback(func as unknown as (...args: any[]) => any, [...dependencies]);
  const debouncedFunction = useMemo<F>(() => throttle(funcToRun, delay) as unknown as F, [funcToRun, delay]);

  return debouncedFunction;
}

interface IUseRightClickOptions {
  ignore?: (event: MouseEvent) => boolean;
  onClose?: () => void;
  onRightClick?: (event: MouseEvent) => void;
}
export function useRightClickMenu(
  bodyRef: React.MutableRefObject<HTMLElement | null>,
  menuRef: React.MutableRefObject<HTMLElement | null>,
  { ignore, onClose, onRightClick }: IUseRightClickOptions
): [number, number, boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    if (!bodyRef.current) return;

    const container = bodyRef.current;

    const handleClick = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return;

      setShowMenu((prev) => {
        if (prev) onClose?.();

        return false;
      });
    };
    const handleRightClick = (event: MouseEvent) => {
      if (!container.contains(event.target as Node) || ignore?.(event)) return;
      event.preventDefault();

      setPosition({ x: event.pageX, y: event.pageY });
      setShowMenu(true);
      onRightClick?.(event);
    };

    document.addEventListener("click", handleClick);
    container.addEventListener("contextmenu", handleRightClick);

    return () => {
      document.removeEventListener("click", handleClick);
      container.removeEventListener("contextmenu", handleRightClick);
    };
  }, [bodyRef, menuRef, ignore, onClose, onRightClick]);

  return [position.x, position.y, showMenu, setShowMenu];
}

export function useToggle(initialValue: boolean = false, runOnChange?: (newValue: boolean) => void): [boolean, (newValue?: any) => void, React.Dispatch<React.SetStateAction<boolean>>] {
  const [state, setState] = useState(() => initialValue);

  const toggle = useCallback(
    (newValue?: any) => {
      setState((prev) => {
        const newState = typeof newValue === "boolean" ? newValue : !prev;

        if (runOnChange && typeof newValue === "boolean") runOnChange(newValue);

        return newState;
      });
    },
    [runOnChange]
  );

  return [state, toggle, setState];
}